import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Info from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CircularProgress from '@mui/material/CircularProgress';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import '../shared.css';

const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
const getFormattedStartDate = () => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
};

function PurchaseIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [fromDate, setFromDate] = useState(getFormattedStartDate);
    const [toDate, setToDate] = useState(getFormattedCurrentDate);
    const history = useHistory();

    const { isAuthenticated, companyId, branchId, permissions, token, logout, userId } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');
    const [isLoading, setIsLoading] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [fileData, setFileData] = useState([]);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) fetchtableData();
    }, [fromDate, toDate, companyId, branchId]);

    const fetchtableData = async () => {
        setData([]);
        try {
            const response = await fetchData('PurchaseApi', `/PurchaseDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Purchase Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }
    };

    const filtered = data.filter(item =>
        Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleDelete = async () => {
        deleteData('PurchaseApi', `${deleteItemId}`, token)
            .then(() => { fetchtableData(); setDeleteItemId(null); setConfirmDialogOpen(false); })
            .catch(err => console.error('Error deleting record:', err));
    };

    const handleActivate = (itemId) => {
        activateData('PurchaseApi', `${itemId}`, token)
            .then(() => fetchtableData())
            .catch(err => console.error('Error activating record:', err));
    };

    const openImportDialog = () => setImportDialogOpen(true);
    const closeImportDialog = () => { setImportDialogOpen(false); setFileData([]); };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            const rowsToRead = sheetData.slice(5);
            const formattedData = rowsToRead
                .filter(row => row[2])
                .map((row) => {
                    const currentDate = new Date();
                    const year = currentDate.getFullYear();
                    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                    const day = String(currentDate.getDate()).padStart(2, '0');
                    const formattedDate = `${year}-${month}-${day}`;
                    return {
                        BillingDate: formattedDate,
                        BillDoc: row[4] ? String(row[4]) : "",
                        Payer: row[5] ? String(row[5]) : "",
                        Name: row[6] ? String(row[6]) : "",
                        Item: row[7] ? String(row[7]) : "",
                        Material: row[8] ? String(row[8]) : "",
                        Description: row[9] ? String(row[9]) : "",
                        ProductHierarchy: row[10] ? String(row[10]) : "",
                        Status: row[11] ? String(row[11]) : "",
                        Qty: row[12] ?? 0,
                        BilledQuantity: row[13] ?? 0,
                        RRP: row[14] ?? 0,
                        ZV05: row[15] ?? 0,
                        ConcessionFee: row[16] ?? 0,
                        NetSalesII: row[17] ?? 0,
                        Vat: row[18] ?? 0,
                        GrossSales: row[19] ?? 0
                    };
                });
            setFileData(formattedData);
        };
        reader.readAsArrayBuffer(file);
    };

    const handleImport = () => {
        setIsLoading(true);
        const CreateData = {
            CompanyId: companyId,
            BranchId: branchId,
            CreatedBy: userId,
            PurchaseImport: fileData
        };
        postData('PurchaseImportApi', CreateData, token)
            .then(() => {
                fetchtableData();
                setIsLoading(false);
                closeImportDialog();
            })
            .catch((error) => {
                console.error('Error posting data:', error);
                setIsLoading(false);
            });
    };

    function formatDate(dateString) {
        return new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }

    return (
        <div className="pg-container">
            {/* ── Header ── */}
            <div className="pg-header">
                <h5 className="pg-title">Purchase</h5>
                <div className="pg-header-actions">
                    {/* Date filters */}
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate}
                                onChange={e => setFromDate(e.target.value)} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate}
                                onChange={e => setToDate(e.target.value)} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={fetchtableData} title="Refresh">
                            <RefreshIcon /> Refresh
                        </button>
                    </div>

                    <div className="pg-action-row">
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearch(v => !v)}>
                            <SearchIcon /> Search
                        </button>
                        {hasCreateMasterPermission && (
                            <button className="pg-btn pg-btn-outline" onClick={openImportDialog}>
                                <ImportExportIcon /> Import
                            </button>
                        )}
                        {hasCreateMasterPermission && (
                            <Link to="/PurchaseCreate" className="pg-btn pg-btn-primary">
                                <AddIcon /> Create New
                            </Link>
                        )}
                    </div>
                    {showSearch && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
                    )}
                </div>
            </div>

            {/* ── Table ── */}
            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>GRN Date</th>
                            <th>GRN Number</th>
                            <th>LR Date</th>
                            <th>LR Number</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                            <th style={{ textAlign: 'right' }}>GST</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                            <th style={{ textAlign: 'right' }}>Total Qty</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0
                            ? <tr><td colSpan={10} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
                            : filtered.map((item, index) => (
                                <tr key={item.Id} style={{ opacity: item.IsActive ? 1 : 0.5 }}>
                                    <td>{index + 1}</td>
                                    <td>{formatDate(item.GRNDate)}</td>
                                    <td>{item.GRNNumber}</td>
                                    <td>{formatDate(item.LRDate)}</td>
                                    <td>{item.LRNumber}</td>
                                    <td style={{ textAlign: 'right' }}>{item.Amount ? item.Amount.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right' }}>{item.GSTAmount ? item.GSTAmount.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{item.TotalAmount ? item.TotalAmount.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right' }}>{item.TotalQuantity}</td>
                                    <td className="actions-cell">
                                        <Link to={`/PurchaseDetails/${item.Id}`}>
                                            <IconButton size="small" title="Details"><Info style={{ color: 'coral', fontSize: 17 }} /></IconButton>
                                        </Link>
                                        {hasEditMasterPermission && item.IsActive && (
                                            <Link to={`/PurchaseEdit/${item.Id}`}>
                                                <IconButton size="small" color="primary" title="Edit"><EditIcon style={{ fontSize: 17 }} /></IconButton>
                                            </Link>
                                        )}
                                        {hasDeleteMasterPermission && (
                                            <IconButton size="small"
                                                title={item.IsActive ? 'Delete' : 'Activate'}
                                                onClick={() => item.IsActive
                                                    ? (setDeleteItemId(item.Id), setConfirmDialogOpen(true))
                                                    : handleActivate(item.Id)}>
                                                {item.IsActive
                                                    ? <DeleteIcon style={{ fontSize: 17, color: '#dc2626' }} />
                                                    : <ActivateIcon style={{ fontSize: 17, color: 'darkolivegreen' }} />}
                                            </IconButton>
                                        )}
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>

            {/* ── Delete Confirm Dialog ── */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Delete Record?</DialogTitle>
                <DialogContent><DialogContentText>Are you sure you want to delete this record?</DialogContentText></DialogContent>
                <DialogActions>
                    <button className="pg-btn pg-btn-danger" onClick={() => setConfirmDialogOpen(false)}>Cancel</button>
                    <button className="pg-btn pg-btn-success" onClick={handleDelete}>Confirm</button>
                </DialogActions>
            </Dialog>

            {/* ── Import Dialog ── */}
            <Dialog open={importDialogOpen} onClose={closeImportDialog} maxWidth="lg" fullWidth>
                <DialogTitle>Import Purchase</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Select an Excel file to import purchase data. The data will be previewed before importing.
                    </DialogContentText>
                    <input type="file" accept=".xlsx, .xls" onChange={handleFileChange} style={{ marginTop: '16px' }} />
                    {fileData.length > 0 && (
                        <div className="pg-table-wrap" style={{ marginTop: 12 }}>
                            <table className="pg-table">
                                <thead>
                                    <tr>
                                        <th>Billing Date</th><th>Bill Doc</th><th>Payer</th><th>Name</th>
                                        <th>Item</th><th>Material</th><th>Description</th><th>Product Hierarchy</th>
                                        <th>Status</th><th>Qty</th><th>Billed Qty</th><th>RRP</th>
                                        <th>ZV05</th><th>Concession Fee</th><th>Net Sales II</th><th>Vat</th><th>Gross Sales</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fileData.map((row, index) => (
                                        <tr key={index}>
                                            <td>{formatDate(row.BillingDate)}</td>
                                            <td>{row.BillDoc}</td><td>{row.Payer}</td><td>{row.Name}</td>
                                            <td>{row.Item}</td><td>{row.Material}</td><td>{row.Description}</td>
                                            <td>{row.ProductHierarchy}</td><td>{row.Status}</td>
                                            <td>{row.Qty}</td><td>{row.BilledQuantity}</td><td>{row.RRP}</td>
                                            <td>{row.ZV05}</td><td>{row.ConcessionFee}</td><td>{row.NetSalesII}</td>
                                            <td>{row.Vat}</td><td>{row.GrossSales}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <button className="pg-btn pg-btn-danger" onClick={closeImportDialog}>Cancel</button>
                    <button className="pg-btn pg-btn-primary" onClick={handleImport} disabled={isLoading || fileData.length === 0}>
                        {isLoading ? <CircularProgress size={16} color="inherit" /> : 'Import'}
                    </button>
                </DialogActions>
            </Dialog>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default PurchaseIndex;
